import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Loading from "../../components/utils/Loading";
import { getSettingsData } from "../../services/settings";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppZoom } from "../../contexts/AppZoomContext";
import { updateUserDisplayZoom } from "../../services/users";
import ConstantsFormPanel from "./ConstantsFormPanel";
import { SETTINGS_CONSTANTS_CONTENT_WIDTH_PX } from "./constantsFormUi";
import {
  CHARACTERIZATION_VIEW_ALL_REPORTS,
  CHARACTERIZATION_VIEW_ZONES,
  normalizeCharacterizationViewMode,
  rememberCharacterizationViewModeFromSettings,
} from "../characterization-2/core/utils/characterizationViewMode";

const DISPLAY_SIZE_OPTIONS = [
  { value: 100, labelKey: "displaySize_large", displayPercent: "+10%" },
  { value: 90, labelKey: "displaySize_normal", displayPercent: "100%" },
  { value: 80, labelKey: "displaySize_compact", displayPercent: "-10%" },
];

const CHARACTERIZATION_VIEW_OPTIONS = [
  {
    value: CHARACTERIZATION_VIEW_ZONES,
    labelKey: "characterization.viewModeZones",
  },
  {
    value: CHARACTERIZATION_VIEW_ALL_REPORTS,
    labelKey: "characterization.viewModeAllReports",
  },
];

const readCharacterizationModuleOn = () => {
  try {
    const modules = JSON.parse(
      localStorage.getItem("corporationModules") || "null",
    );
    return modules ? Boolean(modules.showCharacterizationModule) : true;
  } catch {
    return true;
  }
};

const normalizeSnapshot = ({
  alertNumberOfDays,
  finesSizeThreshold,
  valorizationObjectivePercent,
  displayZoom,
  characterizationViewMode,
}) => ({
  alertNumberOfDays: String(alertNumberOfDays ?? ""),
  finesSizeThreshold: String(finesSizeThreshold ?? ""),
  valorizationObjectivePercent: Number(valorizationObjectivePercent ?? 80),
  displayZoom: Number(displayZoom ?? 90),
  characterizationViewMode: normalizeCharacterizationViewMode(
    characterizationViewMode,
  ),
});

const SuffixNumberField = ({ id, value, onChange, unit, inputProps = {} }) => (
  <div className="settings-constants-suffix-field">
    <input
      id={id}
      type="number"
      className="settings-constants-suffix-field__input"
      value={value}
      onChange={onChange}
      {...inputProps}
    />
    <span className="settings-constants-suffix-field__unit" aria-hidden>
      {unit}
    </span>
  </div>
);

const Constants = ({ token, API, activeTab = "operations" }) => {
  const { t } = useTranslation();
  const { zoomLevel, setZoomLevel } = useAppZoom();
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;
  const [pendingZoomLevel, setPendingZoomLevel] = useState(zoomLevel);
  const [alertNumberOfDays, setAlertNumberOfDays] = useState("");
  const [finesSizeThreshold, setFinesSizeThreshold] = useState("");
  const [valorizationObjectivePercent, setValorizationObjectivePercent] =
    useState(80);
  const [characterizationViewMode, setCharacterizationViewMode] = useState(
    CHARACTERIZATION_VIEW_ZONES,
  );
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const applySettingsData = useCallback((settingsData, displayZoom) => {
    const alertDays = settingsData.alertNumberOfDays ?? "";
    const finesThreshold =
      settingsData.finesSizeThreshold !== undefined &&
      settingsData.finesSizeThreshold !== null
        ? settingsData.finesSizeThreshold
        : 40;
    const rawObjective = settingsData.valorizationObjectivePercent;
    const objectivePercent =
      rawObjective !== undefined && rawObjective !== null && rawObjective !== ""
        ? Number(rawObjective)
        : 80;

    const viewMode = normalizeCharacterizationViewMode(
      settingsData.characterizationViewMode,
    );

    setAlertNumberOfDays(alertDays);
    setFinesSizeThreshold(finesThreshold);
    setValorizationObjectivePercent(objectivePercent);
    setPendingZoomLevel(displayZoom);
    setCharacterizationViewMode(viewMode);
    rememberCharacterizationViewModeFromSettings({
      characterizationViewMode: viewMode,
    });
    setInitialSnapshot(
      normalizeSnapshot({
        alertNumberOfDays: alertDays,
        finesSizeThreshold: finesThreshold,
        valorizationObjectivePercent: objectivePercent,
        displayZoom,
        characterizationViewMode: viewMode,
      }),
    );
  }, []);

  const fetchSettingsData = useCallback(async () => {
    try {
      const response = await getSettingsData();
      const settings = Array.isArray(response.data.settings)
        ? response.data.settings[0]
        : response.data.settings;
      applySettingsData(settings || {}, zoomLevelRef.current);
      setIsInitialLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.error || t("Error loading producers"));
      setIsInitialLoading(false);
    }
  }, [applySettingsData, t]);

  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) return false;
    const current = normalizeSnapshot({
      alertNumberOfDays,
      finesSizeThreshold,
      valorizationObjectivePercent,
      displayZoom: pendingZoomLevel,
      characterizationViewMode,
    });
    return (
      current.alertNumberOfDays !== initialSnapshot.alertNumberOfDays ||
      current.finesSizeThreshold !== initialSnapshot.finesSizeThreshold ||
      current.valorizationObjectivePercent !==
        initialSnapshot.valorizationObjectivePercent ||
      current.displayZoom !== initialSnapshot.displayZoom ||
      current.characterizationViewMode !==
        initialSnapshot.characterizationViewMode
    );
  }, [
    alertNumberOfDays,
    characterizationViewMode,
    finesSizeThreshold,
    initialSnapshot,
    pendingZoomLevel,
    valorizationObjectivePercent,
  ]);

  const handleCancel = () => {
    if (!initialSnapshot) return;
    setAlertNumberOfDays(initialSnapshot.alertNumberOfDays);
    setFinesSizeThreshold(initialSnapshot.finesSizeThreshold);
    setValorizationObjectivePercent(
      initialSnapshot.valorizationObjectivePercent,
    );
    setPendingZoomLevel(initialSnapshot.displayZoom);
    setCharacterizationViewMode(initialSnapshot.characterizationViewMode);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const response = await axios.post(
        `${API}settings/update`,
        {
          alertNumberOfDays,
          finesSizeThreshold:
            finesSizeThreshold !== "" && finesSizeThreshold !== undefined
              ? Number(finesSizeThreshold)
              : finesSizeThreshold,
          valorizationObjectivePercent:
            valorizationObjectivePercent !== "" &&
            valorizationObjectivePercent !== undefined
              ? Math.min(100, Math.max(0, Number(valorizationObjectivePercent)))
              : 80,
          characterizationViewMode,
        },
        { headers: { authorization: `Bearer ${token}` } },
      );

      if (response.status === 200) {
        if (pendingZoomLevel !== zoomLevel) {
          try {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user?._id) {
              await updateUserDisplayZoom(
                { displayZoom: pendingZoomLevel },
                user._id,
              );
            }
            setZoomLevel(pendingZoomLevel);
          } catch (error) {
            toast.error(
              error.response?.data?.error || t("Error saving display size"),
            );
          }
        }

        applySettingsData(
          {
            alertNumberOfDays,
            finesSizeThreshold:
              finesSizeThreshold !== "" && finesSizeThreshold !== undefined
                ? Number(finesSizeThreshold)
                : finesSizeThreshold,
            valorizationObjectivePercent:
              valorizationObjectivePercent !== "" &&
              valorizationObjectivePercent !== undefined
                ? Math.min(
                    100,
                    Math.max(0, Number(valorizationObjectivePercent)),
                  )
                : 80,
            characterizationViewMode,
          },
          pendingZoomLevel,
        );
        toast.success(t("Settings updated successfully"), {
          className: "custom-toast",
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t("Error saving settings"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!token) {
    return null;
  }

  if (isInitialLoading) {
    return <Loading color="#F5F5F5" />;
  }

  return (
    <>
      <form
        id="settings-constants-form"
        className={`settings-constants-form${hasUnsavedChanges ? " settings-constants-form--footer-visible" : ""}`}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <div className="settings-constants-form__column">
          {activeTab === "operations" ? (
            <>
              <ConstantsFormPanel
                title={t("settings_constants_alert_threshold")}
              >
                <div className="settings-constants-field-group">
                  <SuffixNumberField
                    id="alert-threshold-days"
                    value={alertNumberOfDays}
                    onChange={(event) =>
                      setAlertNumberOfDays(event.target.value)
                    }
                    unit={t("settings_constants_unit_days")}
                    inputProps={{ step: "any" }}
                  />
                  <p className="settings-constants-help-text">
                    {t("settings_constants_alert_threshold_help")}
                  </p>
                </div>
              </ConstantsFormPanel>

              <ConstantsFormPanel
                title={t("settings_constants_fines_threshold")}
              >
                <div className="settings-constants-field-group">
                  <SuffixNumberField
                    id="fines-size-threshold"
                    value={finesSizeThreshold}
                    onChange={(event) =>
                      setFinesSizeThreshold(event.target.value)
                    }
                    unit={t("settings_constants_unit_cm")}
                    inputProps={{ step: "1", min: "0" }}
                  />
                  <p className="settings-constants-help-text">
                    {t("settings_constants_fines_threshold_help")}
                  </p>
                </div>
              </ConstantsFormPanel>

              <ConstantsFormPanel
                title={t("settings_constants_sorting_objective")}
              >
                <div className="settings-constants-field-group">
                  <SuffixNumberField
                    id="valorization-objective-percent"
                    value={valorizationObjectivePercent}
                    onChange={(event) =>
                      setValorizationObjectivePercent(event.target.value)
                    }
                    unit="%"
                    inputProps={{ step: "1", min: "0", max: "100" }}
                  />
                  <p className="settings-constants-help-text">
                    {t("settings_constants_sorting_objective_help")}
                  </p>
                </div>
              </ConstantsFormPanel>
            </>
          ) : (
            <>
              <ConstantsFormPanel title={t("displaySize_title")}>
                <div
                  className="settings-constants-radio-group"
                  role="radiogroup"
                  aria-label={t("displaySize_title")}
                >
                  {DISPLAY_SIZE_OPTIONS.map(
                    ({ value, labelKey, displayPercent }) => {
                      const inputId = `display-size-${value}`;
                      return (
                        <div
                          key={value}
                          className="settings-constants-radio-option"
                        >
                          <label
                            htmlFor={inputId}
                            className="waste-flow-radio-label cursor-pointer"
                          >
                            <input
                              id={inputId}
                              type="radio"
                              name="displaySize"
                              className="waste-flow-radio-input"
                              checked={pendingZoomLevel === value}
                              disabled={isSaving}
                              onChange={() => setPendingZoomLevel(value)}
                            />
                            <span>{t(labelKey)}</span>
                          </label>
                          <div className="settings-constants-radio-desc-row">
                            <span
                              className="settings-constants-radio-desc-row__spacer"
                              aria-hidden
                            />
                            <span className="settings-constants-help-text">
                              {displayPercent}
                            </span>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </ConstantsFormPanel>

              {readCharacterizationModuleOn() ? (
                <ConstantsFormPanel title={t("characterization.viewMode")}>
                  <div
                    className="settings-constants-radio-group"
                    role="radiogroup"
                    aria-label={t("characterization.viewMode")}
                  >
                    {CHARACTERIZATION_VIEW_OPTIONS.map(
                      ({ value, labelKey }) => {
                        const inputId = `characterization-view-${value}`;
                        return (
                          <div
                            key={value}
                            className="settings-constants-radio-option"
                          >
                            <label
                              htmlFor={inputId}
                              className="waste-flow-radio-label cursor-pointer"
                            >
                              <input
                                id={inputId}
                                type="radio"
                                name="characterizationViewMode"
                                className="waste-flow-radio-input"
                                checked={characterizationViewMode === value}
                                disabled={isSaving}
                                onChange={() =>
                                  setCharacterizationViewMode(value)
                                }
                              />
                              <span>{t(labelKey)}</span>
                            </label>
                          </div>
                        );
                      },
                    )}
                  </div>
                  <p className="settings-constants-help-text">
                    {t("characterization.viewModeHint")}
                  </p>
                </ConstantsFormPanel>
              ) : null}
            </>
          )}
        </div>
      </form>

      {hasUnsavedChanges && (
        <div className="settings-wizard-footer">
          <div
            className="settings-wizard-footer__inner"
            style={{ maxWidth: SETTINGS_CONSTANTS_CONTENT_WIDTH_PX }}
          >
            <button
              type="button"
              className="modal-button modal-button-cancel"
              disabled={isSaving}
              onClick={handleCancel}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              form="settings-constants-form"
              className={`modal-button modal-button-save ${isSaving ? "disabled" : ""}`}
              disabled={isSaving}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Constants;
